import { useState } from "react";
import {
    Container,
    Box,
    Typography,
    Stack,
    Grid,
    Paper,
    Chip,
    TextField,
    InputAdornment,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Divider,
    Button,
} from "@mui/material";
import { Icon } from "@iconify/react";
import { useTranslation } from "react-i18next";
import Footer from "../components/Footer";
import { COMPANY } from "../config/companyInfo";

// Tekst (title/desc) se čita iz prevoda po `key`; ovde ostaju samo ikona i boja.
const SHORTCUTS = [
    { key: "cancel-refund", icon: "mdi:cash-refund", color: "#FF5630" },
    { key: "exchange", icon: "mdi:swap-horizontal", color: "#3366FF" },
    { key: "resend-confirmation", icon: "mdi:email-send-outline", color: "#00B8D9" },
    { key: "failed-booking", icon: "mdi:alert-circle-outline", color: "#FFAB00" },
    { key: "personal-data", icon: "mdi:shield-account-outline", color: "#36B37E" },
    { key: "missing-route", icon: "mdi:map-marker-question-outline", color: "#6554C0" },
    { key: "boarding-info", icon: "mdi:bus-stop", color: "#1a1a2e" },
    { key: "feedback", icon: "mdi:star-outline", color: "#FFAB00" },
];

export default function HelpCenterPage() {
    const { t } = useTranslation();
    const [search, setSearch] = useState("");

    const faq = t("help.faq", { returnObjects: true });
    const faqList = Array.isArray(faq) ? faq : [];
    const filteredFaq = faqList.filter(
        (item) =>
            item.q.toLowerCase().includes(search.toLowerCase()) ||
            item.a.toLowerCase().includes(search.toLowerCase())
    );

    const handleShortcutClick = () => {
        window.location.href = `tel:${COMPANY.supportPhoneTel}`;
    };

    return (
        <Box>
            {/* ===== HERO ===== */}
            <Box
                sx={{
                    position: "relative",
                    py: { xs: 6, md: 10 },
                    bgcolor: "#1a1a2e",
                    color: "white",
                    overflow: "hidden",
                }}
            >
                <Box
                    sx={{
                        position: "absolute",
                        inset: 0,
                        backgroundImage:
                            "radial-gradient(ellipse at top, rgba(132,169,255,0.25) 0%, transparent 60%)",
                    }}
                />
                <Container maxWidth="md" sx={{ position: "relative", zIndex: 1 }}>
                    <Stack alignItems="center" spacing={2}>
                        <Chip
                            icon={<Icon icon="mdi:help-circle-outline" width={16} />}
                            label={t("help.badge")}
                            sx={{
                                bgcolor: "rgba(255,255,255,0.15)",
                                color: "white",
                                fontWeight: 700,
                                letterSpacing: "2px",
                                fontSize: "0.7rem",
                                "& .MuiChip-icon": { color: "#84A9FF" },
                            }}
                        />
                        <Typography
                            variant="h2"
                            sx={{
                                fontWeight: 900,
                                textAlign: "center",
                                fontSize: { xs: "2rem", md: "3rem" },
                                lineHeight: 1.15,
                            }}
                        >
                            {t("help.heroTitleLine1")}
                            <br />
                            <Box component="span" sx={{ color: "#84A9FF" }}>
                                {t("help.heroTitleLine2")}
                            </Box>
                        </Typography>
                        <Typography
                            variant="body1"
                            sx={{
                                color: "rgba(255,255,255,0.8)",
                                textAlign: "center",
                                maxWidth: 520,
                            }}
                        >
                            {t("help.heroSubtitle")}
                        </Typography>

                        <TextField
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder={t("help.searchPlaceholder")}
                            fullWidth
                            sx={{
                                mt: 2,
                                maxWidth: 560,
                                bgcolor: "white",
                                borderRadius: 2,
                                "& .MuiOutlinedInput-root": {
                                    borderRadius: 2,
                                    fontSize: "1rem",
                                },
                            }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Icon icon="mdi:magnify" width={22} color="#1a1a2e" />
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </Stack>
                </Container>
            </Box>

            {/* ===== SHORTCUTS ===== */}
            <Box sx={{ py: { xs: 6, md: 8 }, bgcolor: "grey.50" }}>
                <Container maxWidth="lg">
                    <Stack alignItems="center" sx={{ mb: 5 }}>
                        <Chip
                            label={t("help.shortcutsBadge")}
                            sx={{
                                mb: 2,
                                fontWeight: 700,
                                letterSpacing: "2px",
                                bgcolor: "primary.lighter",
                                color: "primary.dark",
                                fontSize: "0.75rem",
                            }}
                        />
                        <Typography variant="h4" sx={{ fontWeight: 800, textAlign: "center", mb: 1 }}>
                            {t("help.shortcutsTitle")}
                        </Typography>
                        <Typography variant="body1" color="text.secondary" sx={{ textAlign: "center", maxWidth: 500 }}>
                            {t("help.shortcutsSubtitle")}
                        </Typography>
                    </Stack>

                    <Grid container spacing={2.5}>
                        {SHORTCUTS.map((item) => (
                            <Grid item xs={12} sm={6} md={3} key={item.key}>
                                <Paper
                                    elevation={0}
                                    onClick={handleShortcutClick}
                                    sx={{
                                        p: 3,
                                        borderRadius: 3,
                                        bgcolor: "white",
                                        border: "1px solid",
                                        borderColor: "grey.200",
                                        cursor: "pointer",
                                        height: "100%",
                                        transition: "all 0.25s",
                                        "&:hover": {
                                            borderColor: item.color,
                                            boxShadow: `0 8px 28px ${item.color}22`,
                                            transform: "translateY(-3px)",
                                        },
                                    }}
                                >
                                    <Box
                                        sx={{
                                            width: 48,
                                            height: 48,
                                            borderRadius: 2,
                                            bgcolor: `${item.color}15`,
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            mb: 2,
                                        }}
                                    >
                                        <Icon icon={item.icon} width={26} color={item.color} />
                                    </Box>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
                                        {t(`help.shortcuts.${item.key}.title`)}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                                        {t(`help.shortcuts.${item.key}.desc`)}
                                    </Typography>
                                    <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mt: 2, color: item.color }}>
                                        <Typography variant="caption" sx={{ fontWeight: 700, letterSpacing: 0.5 }}>
                                            {t("help.open")}
                                        </Typography>
                                        <Icon icon="mdi:arrow-right" width={16} />
                                    </Stack>
                                </Paper>
                            </Grid>
                        ))}
                    </Grid>
                </Container>
            </Box>

            {/* ===== FAQ ===== */}
            <Box sx={{ py: { xs: 6, md: 8 }, bgcolor: "white" }}>
                <Container maxWidth="md">
                    <Stack alignItems="center" sx={{ mb: 5 }}>
                        <Chip
                            label={t("help.faqBadge")}
                            sx={{
                                mb: 2,
                                fontWeight: 700,
                                letterSpacing: "2px",
                                bgcolor: "secondary.lighter",
                                color: "secondary.dark",
                                fontSize: "0.75rem",
                            }}
                        />
                        <Typography variant="h4" sx={{ fontWeight: 800, textAlign: "center", mb: 1 }}>
                            {t("help.faqTitle")}
                        </Typography>
                        <Typography variant="body1" color="text.secondary" sx={{ textAlign: "center" }}>
                            {t("help.faqSubtitle")}
                        </Typography>
                    </Stack>

                    {filteredFaq.length === 0 ? (
                        <Paper
                            elevation={0}
                            sx={{ p: 4, textAlign: "center", bgcolor: "grey.50", borderRadius: 3 }}
                        >
                            <Icon icon="mdi:file-search-outline" width={48} color="#9e9e9e" />
                            <Typography variant="h6" sx={{ fontWeight: 700, mt: 2 }}>
                                {t("help.noResultsTitle")}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {t("help.noResultsDesc", { query: search })}
                            </Typography>
                        </Paper>
                    ) : (
                        <Stack spacing={1.5}>
                            {filteredFaq.map((item, idx) => (
                                <Accordion
                                    key={idx}
                                    disableGutters
                                    elevation={0}
                                    sx={{
                                        border: "1px solid",
                                        borderColor: "grey.200",
                                        borderRadius: "12px !important",
                                        "&:before": { display: "none" },
                                        "&.Mui-expanded": {
                                            borderColor: "primary.light",
                                            boxShadow: "0 4px 20px rgba(26,26,46,0.06)",
                                        },
                                    }}
                                >
                                    <AccordionSummary
                                        expandIcon={<Icon icon="mdi:chevron-down" width={24} />}
                                        sx={{ py: 1, px: 2.5 }}
                                    >
                                        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                                            {item.q}
                                        </Typography>
                                    </AccordionSummary>
                                    <AccordionDetails sx={{ px: 2.5, pb: 2.5, pt: 0 }}>
                                        <Divider sx={{ mb: 2 }} />
                                        <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.8 }}>
                                            {item.a}
                                        </Typography>
                                    </AccordionDetails>
                                </Accordion>
                            ))}
                        </Stack>
                    )}
                </Container>
            </Box>

            {/* ===== CONTACT CTA ===== */}
            <Box sx={{ py: { xs: 6, md: 8 }, bgcolor: "grey.50" }}>
                <Container maxWidth="md">
                    <Paper
                        elevation={0}
                        sx={{
                            p: { xs: 3, md: 5 },
                            borderRadius: 4,
                            bgcolor: "white",
                            border: "1px solid",
                            borderColor: "grey.200",
                            textAlign: "center",
                        }}
                    >
                        <Box
                            sx={{
                                width: 64,
                                height: 64,
                                borderRadius: "50%",
                                bgcolor: "primary.lighter",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                mx: "auto",
                                mb: 2,
                            }}
                        >
                            <Icon icon="mdi:headset" width={32} color="#1a1a2e" />
                        </Box>
                        <Typography variant="h5" sx={{ fontWeight: 800, mb: 1 }}>
                            {t("help.contactTitle")}
                        </Typography>
                        <Typography variant="body1" color="text.secondary" sx={{ mb: 3, maxWidth: 460, mx: "auto" }}>
                            {t("help.contactDesc")}
                        </Typography>
                        <Stack
                            direction={{ xs: "column", sm: "row" }}
                            spacing={2}
                            justifyContent="center"
                        >
                            <Button
                                variant="contained"
                                size="large"
                                startIcon={<Icon icon="mdi:email-outline" />}
                                href={`mailto:${COMPANY.supportEmail}`}
                                sx={{ borderRadius: 2, px: 3 }}
                            >
                                {t("help.sendEmail")}
                            </Button>
                            <Button
                                variant="outlined"
                                size="large"
                                startIcon={<Icon icon="mdi:phone" />}
                                href={`tel:${COMPANY.supportPhoneTel}`}
                                sx={{ borderRadius: 2, px: 3 }}
                            >
                                {COMPANY.supportPhone}
                            </Button>
                        </Stack>
                    </Paper>
                </Container>
            </Box>

            <Footer />
        </Box>
    );
}
